import { Label } from "@/components/ui/label";
import VideoWorkflowStep from "../videoWorkflow/videoWorkflowStep";
import { GetVideoResponseType } from "../../../../hooks/useGetVideo";
import { useVideoWorkflow } from "../videoWorkflow/videoWorkflowProvider";
import { Separator } from "@/components/ui/separator";
import { SrclangType, getPlatformLanguages } from "@/db/schema";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import toast from "@/lib/toast";
import { LoaderCircle, Trash } from "lucide-react";
import { client } from "@/lib/hono";
import DeleteTranslationModal from "./deleteTranslationModal";
import { getLanguageLabel, t } from "@/lib/localization";

export default function TranslationsTab({
    videoId,
    captions,
    setReloadVideoKey,
}: {
    videoId: string;
    captions: GetVideoResponseType["data"]["captions"];
    setReloadVideoKey: (key: number) => void;
}) {
    const [translations, setTranslations] = useState<SrclangType[]>([]);
    const [translateInteractions, setTranslateInteractions] = useState(false);
    const [loading, setLoading] = useState(false);
    const [languageToDelete, setLanguageToDelete] =
        useState<SrclangType | null>(null);

    const { shouldShowWorkflowStepComponent } = useVideoWorkflow();

    const platformLanguages = getPlatformLanguages();
    const languagesLeft = platformLanguages.filter(
        (l) => !captions.some((c) => c.srclang === l.code)
    );
    const languagesExist = captions.filter((c) => !c.isSpokenLanguage);

    const generateMoreTranslations = async () => {
        if (!translations.length) {
            toast.error(t("toast.error.no-selected-translations"), {
                keyName: "toast.error.no-selected-translations",
            });
            return;
        }

        setLoading(true);

        const res = await client.api.ai["generate-translations"].$post({
            json: {
                videoId,
                translations,
                translateInteractions,
            },
        });

        setTranslations([]);
        setTranslateInteractions(false);

        if (!res.ok) {
            toast.error(t("toast.error.default"), {
                keyName: "toast.error.default",
            });
        }

        setLoading(false);
    };

    return (
        <>
            <DeleteTranslationModal
                videoId={videoId}
                languageToDelete={languageToDelete}
                setLanguageToDelete={setLanguageToDelete}
                setReloadVideoKey={setReloadVideoKey}
            />

            <div className="mt-4">
                <div className="mb-6">
                    <Label className="">
                        {t(
                            "video-sheet.translations-tab.title",
                            undefined,
                            undefined
                        )}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                        {t(
                            "video-sheet.translations-tab.description",
                            undefined,
                            undefined
                        )}
                    </p>
                </div>

                {shouldShowWorkflowStepComponent("translations") ? (
                    <VideoWorkflowStep
                        stepName="translations"
                        texts={{
                            title: {
                                keyName:
                                    "video-sheet.create-translations-with-ai.title",
                                defaultValue: "יצירת תרגומים עם AI",
                            },
                            description: {
                                keyName:
                                    "video-sheet.create-translations-with-ai.description",
                                defaultValue:
                                    "בחרו את השפות אליהן תרצו לתרגם את הכתוביות",
                            },
                            generateBtn: {
                                keyName:
                                    "video-sheet.generate-translations-with-ai-btn",
                                defaultValue: "תרגום",
                            },
                        }}
                    />
                ) : (
                    <div className="bg-card rounded-md border p-4 flex flex-col w-full">
                        <div className="flex flex-row">
                            <p className="shrink-0 w-[150px] sm:w-[180px] text-sm font-medium">
                                {t(
                                    "video-sheet.translations-tab.existing.label",
                                    undefined,
                                    undefined
                                )}
                            </p>
                            <p className="text-sm">
                                {languagesExist.map((c, i) => (
                                    <span
                                        className="inline-flex items-center"
                                        key={c.srclang}
                                    >
                                        <button
                                            className="me-0.5"
                                            onClick={() =>
                                                setLanguageToDelete(c.srclang)
                                            }
                                        >
                                            <Trash className="size-3.5" />
                                        </button>{" "}
                                        {c.label}
                                        <span className="me-1.5">
                                            {i < languagesExist.length - 1 &&
                                                ", "}
                                            {i === languagesExist.length - 1 &&
                                                "."}
                                        </span>
                                    </span>
                                ))}
                            </p>
                        </div>

                        <Separator className="my-4" />
                        <div className="flex flex-row items-center">
                            <p className="shrink-0 w-[150px] sm:w-[180px] text-sm font-medium">
                                {t(
                                    "video-sheet.translations-tab.create-more.label",
                                    undefined,
                                    undefined
                                )}
                            </p>
                            {languagesLeft.length > 0 ? (
                                <p className="-ms-1">
                                    {languagesLeft.map((language) => {
                                        const selected = !!translations?.find(
                                            (srclang) =>
                                                srclang === language.code
                                        );

                                        return (
                                            <button
                                                className={cn(
                                                    "m-0.5 inline-flex items-center transition rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset",
                                                    selected &&
                                                        "bg-blue-50 hover:bg-blue-100 text-blue-700 ring-blue-500",
                                                    !selected &&
                                                        "bg-gray-50 hover:bg-gray-100 text-gray-600 ring-gray-500/10"
                                                )}
                                                onClick={() => {
                                                    if (selected) {
                                                        setTranslations(
                                                            (prev) =>
                                                                prev.filter(
                                                                    (srclang) =>
                                                                        srclang !==
                                                                        language.code
                                                                )
                                                        );
                                                    } else {
                                                        setTranslations(
                                                            (prev) => [
                                                                ...prev,
                                                                language.code,
                                                            ]
                                                        );
                                                    }
                                                }}
                                                key={language.code}
                                            >
                                                {getLanguageLabel(language.code)}
                                            </button>
                                        );
                                    })}
                                </p>
                            ) : (
                                <p className="text-sm">
                                    {t("common.no-more-options")}
                                </p>
                            )}
                        </div>

                        {languagesLeft.length > 0 && (
                            <label className="mt-4 flex flex-row items-center gap-2 cursor-pointer w-fit">
                                <Checkbox
                                    checked={translateInteractions}
                                    onCheckedChange={(checked) =>
                                        setTranslateInteractions(!!checked)
                                    }
                                />
                                <span className="text-sm">
                                    {t(
                                        "interactions.translate-with-captions.label"
                                    )}
                                </span>
                            </label>
                        )}

                        {languagesLeft.length > 0 && (
                            <Button
                                onClick={generateMoreTranslations}
                                disabled={translations.length === 0 || loading}
                                className="mt-4 w-fit"
                            >
                                {loading ? (
                                    <LoaderCircle className="size-4 animate-spin" />
                                ) : (
                                    t("common.create")
                                )}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </>
    );
}
